import {gsap} from 'gsap'
import {useEffect, useRef, useState} from 'react'

import {announce} from '@github-ui/aria-live'

import Styles from './DecoratedDownloadActionMenu.module.css'

type UseMonaPixelAnimationOptions = {
  enabled: boolean
  prefersReducedMotion: boolean
}

export function useMonaPixelAnimation({enabled, prefersReducedMotion}: UseMonaPixelAnimationOptions) {
  const downloadActionMenuRef = useRef<HTMLDivElement>(null)
  const downloadButtonOverlayRef = useRef<HTMLDivElement>(null)
  const monaPixelRef = useRef<HTMLImageElement>(null)
  const [downloadStatus, setDownloadStatus] = useState('')

  useEffect(() => {
    if (!enabled) return

    const downloadActionMenu = downloadActionMenuRef.current
    const downloadButtonOverlay = downloadButtonOverlayRef.current
    const monaPixel = monaPixelRef.current
    const downloadButton = downloadActionMenu?.querySelector<HTMLAnchorElement>('a')
    const buttonOverlayItems = downloadButtonOverlay?.querySelectorAll<HTMLElement>(`.${Styles.ButtonOverlayItem}`)
    const buttonOverlayText = downloadButtonOverlay?.querySelector<HTMLElement>(`.${Styles.ButtonOverlayText}`)
    if (
      !downloadActionMenu ||
      !downloadButtonOverlay ||
      !monaPixel ||
      !downloadButton ||
      !buttonOverlayItems ||
      !buttonOverlayText
    ) {
      return
    }

    let timeline: gsap.core.Timeline | null = null
    let timer: ReturnType<typeof setTimeout> | null = null
    let statusAnimationFrame: number | null = null

    const showDownloadStatus = () => {
      setDownloadStatus('Downloaded')
      announce('Downloaded')
    }

    const positionMonaPixel = () => {
      const downloadButtonRect = downloadButton.getBoundingClientRect()
      const monaPixelWidth = monaPixel.offsetWidth

      downloadButtonOverlay.style.width = `${downloadButtonRect.width}px`
      downloadButtonOverlay.style.height = `${downloadButtonRect.height}px`
      downloadButtonOverlay.style.borderRadius = window.getComputedStyle(downloadButton).borderRadius
      monaPixel.style.left = `${downloadButtonRect.width / 2 - monaPixelWidth / 2}px`
    }

    const animateMonaPixel = () => {
      positionMonaPixel()
      setDownloadStatus('')
      if (statusAnimationFrame !== null) window.cancelAnimationFrame(statusAnimationFrame)

      downloadButtonOverlay.style.visibility = 'inherit'
      timeline?.kill()

      if (!prefersReducedMotion) {
        timeline = gsap.timeline()
        timeline.set(monaPixel, {autoAlpha: 1})
        timeline.fromTo(
          monaPixel,
          {rotation: -8, scale: 0.8, y: 20},
          {rotation: 0, scale: 1, y: 0, duration: 0.35, ease: 'expo.out'},
          0,
        )

        const delayFactor = 0.04
        const delayLast = delayFactor * (buttonOverlayItems.length - 1)
        const duration1 = 0.5

        for (const [index, item] of buttonOverlayItems.entries()) {
          const delay = delayFactor * index

          timeline.fromTo(
            item,
            {transform: `translateX(-100%)`},
            {
              transform: `translateX(0%)`,
              duration: duration1,
              ease: 'power4.out',
            },
            delay,
          )

          timeline.to(
            item,
            {transform: 'translateX(100%)', duration: 0.5, ease: 'expo.out'},
            delayLast + duration1 - 0.3,
          )
        }

        timeline.add(() => {
          buttonOverlayText.style.visibility = 'inherit'
          statusAnimationFrame = window.requestAnimationFrame(showDownloadStatus)
        }, delayLast)

        timeline.to(
          monaPixel,
          {
            rotation: 8,
            scale: 0.9,
            y: 20,
            autoAlpha: 0,
            duration: 0.2,
            ease: 'power2.in',
            onStart: () => {
              buttonOverlayText.style.visibility = 'hidden'
              downloadButtonOverlay.style.visibility = 'hidden'
            },
          },
          2.8,
        )
      } else {
        if (timer) clearTimeout(timer)
        buttonOverlayText.style.visibility = 'inherit'
        statusAnimationFrame = window.requestAnimationFrame(showDownloadStatus)
        monaPixel.style.visibility = 'inherit'
        monaPixel.style.opacity = '1'

        timer = setTimeout(() => {
          buttonOverlayText.style.visibility = 'hidden'
          downloadButtonOverlay.style.visibility = 'hidden'
          monaPixel.style.visibility = 'hidden'
          monaPixel.style.opacity = '0'
        }, 3000)
      }
    }

    const handleDownloadClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest('a')) return

      animateMonaPixel()
    }

    positionMonaPixel()
    downloadActionMenu.addEventListener('click', handleDownloadClick)
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', positionMonaPixel)

    return () => {
      downloadActionMenu.removeEventListener('click', handleDownloadClick)
      window.removeEventListener('resize', positionMonaPixel)
      if (statusAnimationFrame !== null) window.cancelAnimationFrame(statusAnimationFrame)
      if (timer) clearTimeout(timer)
      timeline?.kill()
    }
  }, [enabled, prefersReducedMotion])

  return {
    downloadActionMenuRef,
    downloadButtonOverlayRef,
    downloadStatus,
    monaPixelRef,
  }
}
